// ---------------------------------------------------------------------------
// Route: /campaigns/spillover — configure per-campaign cap spillover policy.
//
// Thin route shell that renders the spillover form and a short explanation.
// Light theme, emerald to sky gradient heading, Inter, zinc borders.
// ---------------------------------------------------------------------------

import {
	createRoute,
	type AnyRoute,
	type RootRoute,
} from "@tanstack/react-router";
import { SpilloverForm } from "../features/spillover/SpilloverForm";

function CampaignSpilloverPage() {
	return (
		<main className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-6 py-10">
			<header className="flex flex-col gap-2">
				<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					Campaign settings
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Cap spillover
				</h1>
				<p className="text-sm text-zinc-600">
					Choose how a campaign behaves when it reaches its cap. Cancel mode
					rejects additional conversions; allow mode keeps accepting
					over-cap traffic.
				</p>
			</header>

			<SpilloverForm />

			<section className="rounded-lg border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
				<h2 className="mb-2 text-sm font-semibold text-zinc-800">
					How it works
				</h2>
				<ul className="list-disc space-y-1 pl-5">
					<li>
						Policies are scoped to a single campaign and one cap type
						(conversions, clicks, or any).
					</li>
					<li>
						Under-cap events always pass through regardless of the policy.
					</li>
					<li>
						At or above the cap, the policy decides:
						<span className="font-medium"> allow</span> to accept, or
						<span className="font-medium"> cancel</span> to reject.
					</li>
					<li>Existing policies are loaded when you enter the campaign ID.</li>
				</ul>
			</section>
		</main>
	);
}

export function campaignSpilloverRoute<TParent extends AnyRoute | RootRoute>(
	parent: TParent,
) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/campaigns/spillover",
		component: CampaignSpilloverPage,
	});
}

export default CampaignSpilloverPage;
